import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Icon, type IconName } from "~/components/ui/icon";
import { useI18n } from "~/i18n";
import type { MessageKey } from "~/i18n/messages/pt-BR";
import type { Insight } from "~/lib/api/insights";
import { useInsightsFeed } from "~/lib/api/queries.insights";
import "~/routes/calculations/components/insights-summary.css";

const KIND_PLURAL: Record<string, string> = {
  RECALCULATION_READY: "insights.inbox.kind.RECALCULATION_READY",
  RESEARCH_FOUND: "insights.inbox.kind.RESEARCH_FOUND",
  RESEARCH_NEEDS_REVIEW: "insights.inbox.kind.RESEARCH_NEEDS_REVIEW",
  RESEARCH_RUNNING: "insights.inbox.kind.RESEARCH_RUNNING",
  REVIEW_REQUIRED: "insights.inbox.kind.REVIEW_REQUIRED",
  RULE_PUBLISHED: "insights.inbox.kind.RULE_PUBLISHED",
};

function severityIcon(severity: Insight["severity"]): IconName {
  switch (severity) {
    case "POSITIVE":
      return "success";
    case "CAUTION":
      return "warning";
    case "CRITICAL":
      return "flag";
    default:
      return "info";
  }
}

function subjectTo(
  insight: Insight,
):
  | { to: "/calculations/$calculationId"; params: { calculationId: string } }
  | { to: "/calculations/tasks/$taskId"; params: { taskId: string } }
  | { to: "/lots" }
  | null {
  const subject = insight.subject;
  if (!subject) return null;
  if (subject.kind === "CALCULATION") {
    return { to: "/calculations/$calculationId", params: { calculationId: subject.id } };
  }
  if (subject.kind === "FISCAL_TASK") {
    return { to: "/calculations/tasks/$taskId", params: { taskId: subject.id } };
  }
  if (subject.kind === "UPLOAD") {
    return { to: "/lots" };
  }
  return null;
}

function insightText(
  insight: Insight,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
  tPlural: (key: string, count: number, params?: Record<string, string | number>) => string,
  formatCurrency: (value: string | number) => string,
): string {
  if (insight.kind === "SAVINGS_OPPORTUNITY") {
    return t("insights.inbox.kind.SAVINGS_OPPORTUNITY", {
      amount: insight.amount != null ? formatCurrency(insight.amount) : "—",
    });
  }
  const key = KIND_PLURAL[insight.kind];
  const count = insight.count ?? 0;
  if (key) {
    const base = tPlural(key, count, { count });
    if (insight.kind === "REVIEW_REQUIRED" && insight.amount != null) {
      return `${base} · ${t("insights.inbox.atRisk", { amount: formatCurrency(insight.amount) })}`;
    }
    return base;
  }
  return insight.title?.trim() || t("insights.inbox.generic");
}

/**
 * Compact topbar indicator for the insights feed. Hidden when the endpoint is
 * unavailable (404/501) or still pending its first response.
 */
export function InsightsInbox() {
  const { t, tPlural, formatRelative, formatCurrency } = useI18n();
  const { available, feed, isPending } = useInsightsFeed();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDialogElement>(null);
  const panelId = useId();

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target)) close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return;
      event.preventDefault();
      close();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() =>
      panelRef.current
        ?.querySelector<HTMLElement>("a[href], button:not([disabled])")
        ?.focus({ preventScroll: true }),
    );
  }, [open]);

  if (!available || isPending || !feed) return null;

  const unread = feed.unreadCount;
  const badge = unread > 9 ? t("insights.inbox.badgeMore") : unread > 0 ? String(unread) : null;
  const items = feed.items.slice(0, 6);

  return (
    <div className="insights-inbox" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="insights-inbox__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={
          unread > 0
            ? t("insights.inbox.triggerUnread", { count: unread > 9 ? "9+" : String(unread) })
            : t("insights.inbox.trigger")
        }
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="flag" size={18} />
        {badge ? (
          <span className="insights-inbox__badge" aria-hidden="true">
            {badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <dialog
          open
          className="insights-inbox__panel"
          id={panelId}
          aria-label={t("insights.inbox.title")}
          ref={panelRef}
        >
          <div className="insights-inbox__panel-head">
            <h2 className="insights-inbox__panel-title">{t("insights.inbox.title")}</h2>
          </div>
          {items.length === 0 ? (
            <p className="insights-inbox__empty">{t("insights.inbox.empty")}</p>
          ) : (
            <ul className="insights-inbox__list">
              {items.map((item) => {
                const href = subjectTo(item);
                const unreadDot = item.readAt == null;
                const body = (
                  <>
                    <span
                      className="insights-inbox__item-icon"
                      data-tone={item.severity}
                      aria-hidden="true"
                    >
                      <Icon name={severityIcon(item.severity)} size={16} />
                    </span>
                    <span className="insights-inbox__item-copy">
                      <span className="insights-inbox__item-text">
                        {insightText(item, t, tPlural, formatCurrency)}
                      </span>
                      {item.subject?.label ? (
                        <span className="insights-inbox__item-meta">{item.subject.label}</span>
                      ) : null}
                    </span>
                    <span className="insights-inbox__item-side">
                      <span>{formatRelative(item.createdAt)}</span>
                      {unreadDot ? (
                        <>
                          <span className="insights-inbox__unread" aria-hidden="true" />
                          <span className="sr-only">{t("insights.inbox.unread")}</span>
                        </>
                      ) : null}
                    </span>
                  </>
                );

                return (
                  <li key={item.id}>
                    {href ? (
                      href.to === "/lots" ? (
                        <Link
                          className="insights-inbox__item"
                          to="/lots"
                          onClick={() => close(false)}
                        >
                          {body}
                        </Link>
                      ) : href.to === "/calculations/$calculationId" ? (
                        <Link
                          className="insights-inbox__item"
                          to="/calculations/$calculationId"
                          params={href.params}
                          onClick={() => close(false)}
                        >
                          {body}
                        </Link>
                      ) : (
                        <Link
                          className="insights-inbox__item"
                          to="/calculations/tasks/$taskId"
                          params={href.params}
                          onClick={() => close(false)}
                        >
                          {body}
                        </Link>
                      )
                    ) : (
                      <div className="insights-inbox__item">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </dialog>
      ) : null}
    </div>
  );
}
